import $http from '@/utils/axios';
import type { ApiResponse, PageResult } from '@/types/common';
import type {
  OmniAccount,
  OmniConversation,
  OmniMessage,
  OmniPlatform,
  OmniItemInfo,
  QuickReplyItem,
  QuickReplyCategory,
  AutoReplyRule,
  GreetingRule,
  XianyuItemSummary,
  XianyuItemDetail,
  XianyuSoldOrderItem,
  XianyuOrderDetail,
  XianyuUserProfile,
} from '@/types/omni';
import type {
  PddCsUser,
  PddRecommendGoodsItem,
  PddUserOrder,
  PddOrderQuantity,
  PddPromoPriceResult,
  PddShipOrderResult,
  PddSyncConversationsResult,
  PddSyncMessagesResult,
} from '@/types/pdd';

export interface OmniAccountQueryParams {
  platform?: OmniPlatform | 'all';
  [key: string]: any;
}

export interface OmniConversationQueryParams {
  platform?: OmniPlatform | 'all';
  accountId?: string;
  status?: string;
  keyword?: string;
  page?: number;
  pageSize?: number;
  [key: string]: any;
}

export interface OmniMessageQueryParams {
  page?: number;
  pageSize?: number;
  beforeTime?: number;
  afterTime?: number;
  [key: string]: any;
}

export interface CandidateManager {
  username: string;
  nickname?: string;
  roleCode?: string;
  [key: string]: any;
}

export const omniApi = {
  /**
   * 获取全渠道账号与店铺画像列表
   */
  getAccounts(params: OmniAccountQueryParams = {}): Promise<ApiResponse<OmniAccount[]>> {
    return $http.get('/omni/accounts', { params });
  },

  /**
   * 启动指定平台的扫码登录或已有店铺续期
   */
  startQrCodeLogin(
    platform: OmniPlatform = 'pdd',
    params?: { shopId?: string; proxyUrl?: string }
  ): Promise<ApiResponse<{ sessionId: string; qrCodeUrl?: string; qrCodeBase64?: string }>> {
    return $http.post('/omni/accounts/login/qrcode', { platform, ...(params || {}) });
  },

  /**
   * 轮询扫码登录状态
   */
  getQrCodeStatus(sessionId: string, platform: OmniPlatform = 'pdd'): Promise<ApiResponse<any>> {
    return $http.get(`/omni/accounts/login/qrcode/${encodeURIComponent(sessionId)}/status`, {
      params: { platform }
    });
  },

  /**
   * 取消并立即销毁扫码会话与无头浏览器
   */
  cancelQrCodeLogin(platform: OmniPlatform = 'pdd', sessionId?: string): Promise<ApiResponse<any>> {
    return $http.post('/omni/accounts/login/qrcode/cancel', { platform, sessionId });
  },

  /**
   * 测试 SOCKS 代理连通性
   */
  testProxy(proxyUrl: string): Promise<ApiResponse<{ valid: boolean; message: string; latency?: number }>> {
    return $http.post('/omni/accounts/proxy/test', { proxyUrl });
  },

  /**
   * 修改指定店铺/账号绑定的代理配置
   */
  updateAccountProxy(platform: OmniPlatform, accountId: string, proxyUrl: string): Promise<ApiResponse<any>> {
    return $http.put(`/omni/accounts/${encodeURIComponent(platform)}/${encodeURIComponent(accountId)}/proxy`, { proxyUrl });
  },

  /**
   * 连接指定账号长连接
   */
  connectAccount(platform: OmniPlatform, accountId: string): Promise<ApiResponse<any>> {
    return $http.post(`/omni/accounts/${encodeURIComponent(platform)}/${encodeURIComponent(accountId)}/connect`);
  },

  /**
   * 断开指定账号长连接
   */
  disconnectAccount(platform: OmniPlatform, accountId: string): Promise<ApiResponse<any>> {
    return $http.post(`/omni/accounts/${encodeURIComponent(platform)}/${encodeURIComponent(accountId)}/disconnect`);
  },

  /**
   * 刷新/续期指定账号凭证
   */
  refreshCookies(platform: OmniPlatform, accountId: string): Promise<ApiResponse<any>> {
    return $http.post(`/omni/accounts/${encodeURIComponent(platform || 'pdd')}/${encodeURIComponent(accountId)}/refresh-cookies`);
  },

  /**
   * 删除指定账号
   */
  deleteAccount(platform: OmniPlatform, accountId: string): Promise<ApiResponse<any>> {
    return $http.delete(`/omni/accounts/${encodeURIComponent(platform)}/${encodeURIComponent(accountId)}`);
  },

  /**
   * 获取系统可分配管理人员候选用户列表
   */
  getCandidateManagers(): Promise<ApiResponse<CandidateManager[]>> {
    return $http.get('/omni/accounts/candidate-managers');
  },

  /**
   * 分配店铺管理人员
   */
  assignShopManager(platform: OmniPlatform, accountId: string, data: { managerUsername: string; managerName?: string }): Promise<ApiResponse<any>> {
    return $http.put(`/omni/accounts/${encodeURIComponent(platform)}/${encodeURIComponent(accountId)}/manager`, data);
  },

  /**
   * 获取全渠道聚合会话列表
   */
  getConversations(params: OmniConversationQueryParams = {}): Promise<ApiResponse<PageResult<OmniConversation>>> {
    return $http.get('/omni/chat/conversations', { params });
  },

  /**
   * 获取指定渠道会话的历史消息流
   */
  getConversationMessages(platform: OmniPlatform, conversationId: string, params: OmniMessageQueryParams = {}): Promise<ApiResponse<OmniMessage[]>> {
    return $http.get(`/omni/chat/conversations/${encodeURIComponent(platform || 'pdd')}/${encodeURIComponent(conversationId)}/messages`, { params });
  },

  /**
   * 标记会话已读
   */
  markConversationRead(platform: OmniPlatform, conversationId: string): Promise<ApiResponse<any>> {
    return $http.post(`/omni/chat/conversations/${encodeURIComponent(platform || 'pdd')}/${encodeURIComponent(conversationId)}/read`);
  },

  /**
   * 发送人工客服消息
   */
  sendMessage(platform: OmniPlatform, conversationId: string, data: Partial<OmniMessage> | Record<string, any>): Promise<ApiResponse<OmniMessage>> {
    return $http.post(`/omni/chat/conversations/${encodeURIComponent(platform || 'pdd')}/${encodeURIComponent(conversationId)}/send`, data);
  },

  /**
   * 撤回已发送消息
   */
  recallMessage(platform: OmniPlatform = 'pdd', conversationId: string, data: Record<string, any> = {}): Promise<ApiResponse<any>> {
    return $http.post(`/omni/chat/conversations/${encodeURIComponent(platform)}/${encodeURIComponent(conversationId)}/recall`, data);
  },

  /**
   * 执行平台专属动作
   */
  executeAction<T = any>(platform: OmniPlatform, actionName: string, payload: Record<string, any> = {}): Promise<ApiResponse<T>> {
    return $http.post('/omni/chat/action', { platform: platform || 'pdd', actionName, payload });
  },

  // ==================== 闲鱼专属 API ====================
  /**
   * 获取闲鱼卖家在售宝贝列表
   */
  getXianyuSellerItems(accountId: string, pageNumber = 1, pageSize = 20): Promise<ApiResponse<XianyuItemSummary[]>> {
    return omniApi.executeAction<XianyuItemSummary[]>('xianyu', 'get_seller_items', { accountId, pageNumber, pageSize });
  },

  /**
   * 获取闲鱼宝贝详情快照
   */
  getXianyuItemDetail(accountId: string, itemId: string | number): Promise<ApiResponse<XianyuItemDetail>> {
    return omniApi.executeAction<XianyuItemDetail>('xianyu', 'get_item_detail', { accountId, itemId });
  },

  /**
   * 获取闲鱼卖家已卖出的订单列表
   */
  getXianyuSoldOrders(accountId: string, pageNumber = 1, pageSize = 20): Promise<ApiResponse<XianyuSoldOrderItem[]>> {
    return omniApi.executeAction<XianyuSoldOrderItem[]>('xianyu', 'get_sold_orders', { accountId, pageNumber, pageSize });
  },

  /**
   * 获取闲鱼订单详细信息
   */
  getXianyuOrderDetail(accountId: string, orderId: string | number): Promise<ApiResponse<XianyuOrderDetail>> {
    return omniApi.executeAction<XianyuOrderDetail>('xianyu', 'get_order_detail', { accountId, orderId });
  },

  /**
   * 获取闲鱼买家用户画像（昵称与头像）
   */
  getXianyuBuyerProfile(accountId: string, userId: string): Promise<ApiResponse<XianyuUserProfile>> {
    return omniApi.executeAction<XianyuUserProfile>('xianyu', 'get_buyer_profile', { accountId, userId });
  },

  syncShopConversations(platform: OmniPlatform = 'pdd', accountId: string = 'all'): Promise<ApiResponse<PddSyncConversationsResult>> {
    return omniApi.executeAction<PddSyncConversationsResult>(platform, 'sync_conversations', { shopId: accountId, accountId });
  },

  syncConversationMessages(platform: OmniPlatform = 'pdd', conversationId: string, params: Record<string, any> = {}): Promise<ApiResponse<PddSyncMessagesResult>> {
    return omniApi.executeAction<PddSyncMessagesResult>(platform, 'sync_messages', { conversationId, ...params });
  },

  autoCreateOrderFromChat(platform: OmniPlatform = 'pdd', conversationId: string): Promise<ApiResponse<any>> {
    return omniApi.executeAction(platform, 'auto_create_order', { conversationId });
  },

  getShopCsList(platform: OmniPlatform = 'pdd', accountId: string): Promise<ApiResponse<PddCsUser[]>> {
    return omniApi.executeAction<PddCsUser[]>(platform, 'get_cs_list', { shopId: accountId, accountId });
  },

  transferConversation(platform: OmniPlatform = 'pdd', conversationId: string, data: Record<string, any> = {}): Promise<ApiResponse<{ success?: boolean }>> {
    return omniApi.executeAction<{ success?: boolean }>(platform, 'transfer_conversation', { conversationId, ...data });
  },

  getPromoPrice(platform: OmniPlatform = 'pdd', conversationId: string, params: Record<string, any> = {}): Promise<ApiResponse<PddPromoPriceResult[]>> {
    return omniApi.executeAction<PddPromoPriceResult[]>(platform, 'get_promo_price', { conversationId, ...params });
  },

  getShopGoods(platform: OmniPlatform = 'pdd', accountId: string, params: Record<string, any> = {}): Promise<ApiResponse<PddRecommendGoodsItem[]>> {
    return omniApi.executeAction<PddRecommendGoodsItem[]>(platform, 'get_shop_goods', { shopId: accountId, accountId, ...params });
  },

  sendGoodsCard(platform: OmniPlatform = 'pdd', conversationId: string, payload: Record<string, any> = {}): Promise<ApiResponse<{ success?: boolean }>> {
    return omniApi.executeAction<{ success?: boolean }>(platform, 'invite_order', { conversationId, ...payload });
  },

  getConversationOrders(platform: OmniPlatform = 'pdd', conversationId: string, params: Record<string, any> = {}): Promise<ApiResponse<PddUserOrder[]>> {
    return omniApi.executeAction<PddUserOrder[]>(platform, 'get_conversation_orders', { conversationId, ...params });
  },

  getConversationOrderQuantity(platform: OmniPlatform = 'pdd', conversationId: string): Promise<ApiResponse<PddOrderQuantity>> {
    return omniApi.executeAction<PddOrderQuantity>(platform, 'get_order_quantity', { conversationId });
  },

  shipOrder(platform: OmniPlatform = 'pdd', accountId: string, orderSn: string): Promise<ApiResponse<PddShipOrderResult>> {
    return omniApi.executeAction<PddShipOrderResult>(platform, 'ship_order', { shopId: accountId, accountId, orderSn });
  },


  /**
   * 获取咨询商品详情
   */
  getItemContext(platform: OmniPlatform, accountId: string, itemId: string): Promise<ApiResponse<OmniItemInfo>> {
    return $http.get(`/omni/chat/context/${encodeURIComponent(platform)}/${encodeURIComponent(accountId)}/item/${encodeURIComponent(itemId)}`);
  },

  // ==================== 全渠道快捷常用语 ====================
  getQuickReplies(params: Record<string, any> = {}): Promise<ApiResponse<QuickReplyItem[]>> {
    return $http.get('/omni/chat/quick-replies', { params });
  },

  getQuickReplyCategories(params: Record<string, any> = {}): Promise<ApiResponse<QuickReplyCategory[]>> {
    return $http.get('/omni/chat/quick-replies/categories', { params });
  },

  createQuickReply(data: Partial<QuickReplyItem>): Promise<ApiResponse<QuickReplyItem>> {
    return $http.post('/omni/chat/quick-replies', data);
  },

  updateQuickReply(id: string | number, data: Partial<QuickReplyItem>): Promise<ApiResponse<QuickReplyItem>> {
    return $http.put(`/omni/chat/quick-replies/${encodeURIComponent(id)}`, data);
  },

  deleteQuickReply(id: string | number): Promise<ApiResponse<any>> {
    return $http.delete(`/omni/chat/quick-replies/${encodeURIComponent(id)}`);
  },

  resetDefaultQuickReplies(data: Record<string, any> = {}): Promise<ApiResponse<any>> {
    return $http.post('/omni/chat/quick-replies/reset-defaults', data);
  },

  // ==================== 全渠道自动回复规则 ====================
  getAutoReplyRules(params: Record<string, any> = {}): Promise<ApiResponse<AutoReplyRule[]>> {
    return $http.get('/omni/chat/auto-reply/rules', { params });
  },

  createAutoReplyRule(data: Partial<AutoReplyRule>): Promise<ApiResponse<AutoReplyRule>> {
    return $http.post('/omni/chat/auto-reply/rules', data);
  },

  updateAutoReplyRule(id: string | number, data: Partial<AutoReplyRule>): Promise<ApiResponse<AutoReplyRule>> {
    return $http.put(`/omni/chat/auto-reply/rules/${encodeURIComponent(id)}`, data);
  },

  deleteAutoReplyRule(id: string | number): Promise<ApiResponse<any>> {
    return $http.delete(`/omni/chat/auto-reply/rules/${encodeURIComponent(id)}`);
  },

  toggleAutoReplyRule(id: string | number): Promise<ApiResponse<any>> {
    return $http.post(`/omni/chat/auto-reply/rules/${encodeURIComponent(id)}/toggle`);
  },

  testAutoReplyRule(data: Record<string, any>): Promise<ApiResponse<any>> {
    return $http.post('/omni/chat/auto-reply/test', data);
  },

  // ==================== 开场白自动回复规则 ====================
  getGreetingRules(params: Record<string, any> = {}): Promise<ApiResponse<GreetingRule[]>> {
    return $http.get('/omni/chat/auto-reply/greetings', { params });
  },

  createGreetingRule(data: Partial<GreetingRule>): Promise<ApiResponse<GreetingRule>> {
    return $http.post('/omni/chat/auto-reply/greetings', data);
  },

  updateGreetingRule(id: string | number, data: Partial<GreetingRule>): Promise<ApiResponse<GreetingRule>> {
    return $http.put(`/omni/chat/auto-reply/greetings/${encodeURIComponent(id)}`, data);
  },

  deleteGreetingRule(id: string | number): Promise<ApiResponse<any>> {
    return $http.delete(`/omni/chat/auto-reply/greetings/${encodeURIComponent(id)}`);
  },

  toggleGreetingRule(id: string | number): Promise<ApiResponse<{ _id: string; enabled: boolean }>> {
    return $http.post(`/omni/chat/auto-reply/greetings/${encodeURIComponent(id)}/toggle`);
  },

  // ==================== 自动下单日志与通用代理 ====================
  getAutoOrderLogs(params: Record<string, any> = {}): Promise<ApiResponse<any>> {
    return $http.get('/omni/chat/auto-order/logs', { params });
  },

  // ==================== 聊天记录咨询热词运营分析 ====================
  getHotKeywordsAnalysis(params: Record<string, any> = {}): Promise<ApiResponse<any>> {
    return $http.get('/omni/chat/analysis/hot-keywords', { params });
  },

  getImageProxyUrl(url: string): string {
    return `/api/omni/chat/image-proxy?url=${encodeURIComponent(url)}`;
  },

  /**
   * 获取文件带鉴权签名的下载地址 (plateau/file/sign)
   */
  signFileUrl(params: {
    platform?: OmniPlatform;
    conversationId?: string;
    accountId?: string;
    shopId?: string;
    fileUrl: string;
  }): Promise<ApiResponse<{ url: string }>> {
    return $http.post('/omni/chat/file/sign', {
      platform: params.platform || 'pdd',
      conversationId: params.conversationId,
      shopId: params.shopId || params.accountId,
      url: params.fileUrl,
      fileUrl: params.fileUrl,
    });
  },

  /**
   * 获取后端代理流下载地址
   */
  getFileDownloadUrl(params: {
    platform?: OmniPlatform;
    conversationId?: string;
    shopId?: string;
    url: string;
    fileName?: string;
  }): string {
    const query = new URLSearchParams({
      platform: params.platform || 'pdd',
      url: params.url,
      ...(params.conversationId ? { conversationId: params.conversationId } : {}),
      ...(params.shopId ? { shopId: params.shopId } : {}),
      ...(params.fileName ? { fileName: params.fileName } : {}),
    });
    return `/api/omni/chat/file-download?${query.toString()}`;
  }
};

export default omniApi;
